// Fechas en la zona de referencia: America/Montevideo (UTC-3, sin horario de
// verano desde 2015). Critico porque el Worker corre en UTC: una tarea cargada
// a las 22:30 hora local tiene que caer en el dia correcto.
//
// Estrategia (heredada de PediPan): se desplaza el instante UTC 3 horas hacia
// atras para que los getters UTC lean la "hora de pared" de Montevideo.
//
// Todo lo que calcula fechas vive aca y va con test (AGENTS.md seccion 5).

import type { Periodicidad } from "./tipos";

const OFFSET_MIN = -180; // -03:00
const MS_DIA = 24 * 60 * 60 * 1000;

function pad(n: number): string {
  return n < 10 ? "0" + n : String(n);
}

function formatearDia(d: Date): string {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** Fecha de hoy (YYYY-MM-DD) segun la hora de Montevideo. */
export function hoyMontevideo(ahora: Date = new Date()): string {
  return formatearDia(new Date(ahora.getTime() + OFFSET_MIN * 60 * 1000));
}

/** Valida que un string sea una fecha 'YYYY-MM-DD' plausible. */
export function esFechaValida(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [a, m, d] = s.split("-").map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  return a >= 2000 && a <= 2100;
}

/** Suma (o resta) dias a una fecha ISO y devuelve otra fecha ISO. */
export function sumarDias(iso: string, dias: number): string {
  const [a, m, d] = iso.split("-").map(Number);
  return formatearDia(new Date(Date.UTC(a, m - 1, d) + dias * MS_DIA));
}

/** Dias entre dos fechas ISO. Negativo = 'iso' ya paso. */
export function diasHasta(iso: string, hoy: string): number {
  const [a1, m1, d1] = iso.split("-").map(Number);
  const [a2, m2, d2] = hoy.split("-").map(Number);
  return Math.round((Date.UTC(a1, m1 - 1, d1) - Date.UTC(a2, m2 - 1, d2)) / MS_DIA);
}

/** 'YYYY-MM-DD' -> 'dd/mm/aaaa'. Solo para mostrar, nunca para guardar. */
export function formatoLocal(iso: string): string {
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

/** Ultimo dia de un mes (mes va de 1 a 12). Contempla anios bisiestos. */
function ultimoDiaDelMes(anio: number, mes: number): number {
  return new Date(Date.UTC(anio, mes, 0)).getUTCDate();
}

/**
 * Suma meses recortando el dia al ultimo del mes destino: el 31 de enero mas
 * un mes es el 28 (o 29) de febrero, porque el 31 de febrero no existe.
 */
export function sumarMeses(iso: string, meses: number): string {
  const [a, m, d] = iso.split("-").map(Number);
  const total = m - 1 + meses;
  const anio = a + Math.floor(total / 12);
  const mes = ((total % 12) + 12) % 12 + 1;
  return `${anio}-${pad(mes)}-${pad(Math.min(d, ultimoDiaDelMes(anio, mes)))}`;
}

function mesesDe(periodicidad: Periodicidad): number {
  if (periodicidad === "MENSUAL") return 1;
  if (periodicidad === "TRIMESTRAL") return 3;
  return 12;
}

/** Un periodo hacia adelante. UNICO no se repite: devuelve null. */
export function proximaFecha(iso: string, periodicidad: Periodicidad): string | null {
  if (periodicidad === "UNICO") return null;
  return sumarMeses(iso, mesesDe(periodicidad));
}

/**
 * Proximo vencimiento que todavia no paso. Si un recordatorio quedo atrasado
 * varios periodos, los saltea de una.
 *
 * Cuenta siempre desde la fecha original, nunca desde la ultima calculada: si
 * avanzara sobre el resultado ya recortado, un recordatorio del 31 de enero se
 * correria al 28 de febrero y de ahi no volveria nunca al 31.
 */
export function proximaFechaVigente(
  iso: string,
  periodicidad: Periodicidad,
  hoy: string,
): string | null {
  if (periodicidad === "UNICO") return null;
  const paso = mesesDe(periodicidad);
  for (let n = 1; n <= 400; n++) {
    const f = sumarMeses(iso, paso * n);
    if (diasHasta(f, hoy) >= 0) return f;
  }
  return sumarMeses(iso, paso);
}

/** Domingo = 0, como en Date.getUTCDay(). */
export const DIAS_SEMANA = [
  "domingo",
  "lunes",
  "martes",
  "miercoles",
  "jueves",
  "viernes",
  "sabado",
] as const;

/**
 * El proximo dia de semana que venga, sin contar hoy.
 *
 * Decir "el lunes" un lunes es el lunes que viene, no hoy: si fuera hoy, uno
 * dice "hoy". Por eso un delta de 0 se convierte en 7.
 */
export function proximoDiaSemana(hoy: string, dia: number): string {
  const [a, m, d] = hoy.split("-").map(Number);
  const actual = new Date(Date.UTC(a, m - 1, d)).getUTCDay();
  const delta = (dia - actual + 7) % 7;
  return sumarDias(hoy, delta === 0 ? 7 : delta);
}

/** Ultimo dia del mes de una fecha ISO. */
export function finDeMes(iso: string): string {
  const [a, m] = iso.split("-").map(Number);
  return `${a}-${pad(m)}-${pad(ultimoDiaDelMes(a, m))}`;
}

export type Urgencia = "VENCIDO" | "HOY" | "SEMANA" | "LEJOS";

/** Que tan encima esta un vencimiento. Null si la tarea no tiene fecha. */
export function urgencia(vence: string | null, hoy: string): Urgencia | null {
  if (!vence) return null;
  const d = diasHasta(vence, hoy);
  if (d < 0) return "VENCIDO";
  if (d === 0) return "HOY";
  if (d <= 7) return "SEMANA";
  return "LEJOS";
}
